import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Mountain, Target, Zap, Shield, Compass, Tent, Play, ArrowRight } from 'lucide-react';
import { TechLabel } from '../common/TacticalCard';
import { VideoCard } from './VideoBackground';

// Icon mapping
const iconMap = {
  Mountain,
  Target,
  Zap,
  Shield,
  Compass,
  Tent
};

const UseCaseCard = ({ 
  item, 
  index, 
  isActive, 
  onClick 
}) => {
  const Icon = iconMap[item.icon] || Mountain;
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`group relative cursor-pointer ${isActive ? 'z-10' : ''}`}
      onClick={onClick}
    >
      <div 
        className={`relative overflow-hidden rounded-2xl transition-all duration-500 ${
          isActive 
            ? 'ring-2 ring-vav-gold shadow-[0_0_40px_rgba(196,168,75,0.3)]' 
            : 'ring-1 ring-vav-border hover:ring-vav-gold/50'
        }`}
      >
        {/* Image/Video Container */}
        <div className="aspect-[4/3] overflow-hidden">
          {item.video_url ? (
            <VideoCard 
              videoUrl={item.video_url}
              posterImage={item.image_url}
              title={item.title}
              className="w-full h-full"
            />
          ) : (
            <motion.img
              src={item.image_url}
              alt={item.title}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.6 }}
            />
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          
          {/* Scan line effect on active */}
          {isActive && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vav-gold to-transparent"
                animate={{ y: ['0%', '400%', '0%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
            </motion.div>
          )}
        </div>
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {/* Icon with pulse effect */}
          <motion.div 
            className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${
              isActive 
                ? 'bg-vav-gold text-black' 
                : 'bg-vav-bg/80 border border-vav-border text-vav-gold'
            }`}
            animate={isActive ? { scale: [1, 1.1, 1] } : {}}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <Icon className="w-6 h-6" />
          </motion.div>

          {/* Title */}
          <h3 className={`font-heading text-2xl mb-2 transition-colors duration-300 ${
            isActive ? 'text-vav-gold' : 'text-white group-hover:text-vav-gold'
          }`}>
            {item.title}
          </h3>

          {/* Description - Only show when active */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <p className="text-vav-fg-muted text-sm leading-relaxed mb-3">
                  {item.description}
                </p>
                <Link 
                  to={`/use-cases/${item.slug || item.usecase_id}`}
                  className="inline-flex items-center gap-2 text-vav-gold text-sm hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Devamını Oku
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Corner accents */}
        <div className={`absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 transition-colors duration-300 ${
          isActive ? 'border-vav-gold' : 'border-vav-gold/20'
        }`} />
        <div className={`absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 transition-colors duration-300 ${
          isActive ? 'border-vav-gold' : 'border-vav-gold/20'
        }`} />
        <div className={`absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 transition-colors duration-300 ${
          isActive ? 'border-vav-gold' : 'border-vav-gold/20'
        }`} />
        <div className={`absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 transition-colors duration-300 ${
          isActive ? 'border-vav-gold' : 'border-vav-gold/20'
        }`} />
      </div>
    </motion.div>
  );
};

const UseCasesShowcase = ({ useCases = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Default use cases if none provided
  const defaultCases = [
    {
      usecase_id: '1',
      icon: 'Mountain',
      title: 'OUTDOOR',
      description: 'Dağ, orman, kamp - doğanın her alanında güvenle. Zorlu arazi koşullarında maksimum performans.',
      image_url: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80',
      video_url: ''
    },
    {
      usecase_id: '2',
      icon: 'Target',
      title: 'OPERASYONEL',
      description: 'Güvenlik, savunma ve profesyonel kullanım. Askeri standartlarda dayanıklılık.',
      image_url: 'https://images.unsplash.com/photo-1516911396153-4a4f5e3e7f4a?w=800&q=80',
      video_url: ''
    },
    {
      usecase_id: '3',
      icon: 'Zap',
      title: 'GÜNLÜK',
      description: 'Şehir hayatında şık ve işlevsel tarz. Konfor ve stil bir arada.',
      image_url: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=800&q=80',
      video_url: ''
    }
  ];

  const cases = useCases.length > 0 ? useCases : defaultCases;

  return (
    <section 
      ref={sectionRef}
      className="relative py-24 bg-vav-bg overflow-hidden" 
      data-testid="usecases-showcase"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 animated-grid opacity-30" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-vav-gold/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              y: [-20, 20, -20],
              opacity: [0.2, 0.6, 0.2]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <TechLabel variant="accent" className="mb-4">[KULLANIM ALANLARI]</TechLabel>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-vav-fg mb-4">
            PROOF / USE-CASES
          </h2>
          <p className="text-vav-fg-muted max-w-2xl mx-auto">
            Her ortamda, her koşulda yanınızda. Taktik giyimin sınırlarını keşfedin.
          </p>
        </motion.div>

        {/* Use Case Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {cases.map((item, index) => (
            <UseCaseCard
              key={item.usecase_id}
              item={item}
              index={index}
              isActive={activeIndex === index}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center gap-3 mt-12">
          {cases.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeIndex === index 
                  ? 'bg-vav-gold w-8' 
                  : 'bg-vav-border hover:bg-vav-gold/50'
              }`}
              data-testid={`usecase-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCasesShowcase;
